import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { AppError } from '@models/app-error';

@Injectable({
  providedIn: 'root',
})
export class ErrorLoggingService {
  private zone = inject(NgZone);

  /**
   * Logs an error along with the current local storage state, excluding the sensitive entries.
   */
  public logErrorWithStorage(errorMessage: string): void {
    const EXCLUDED_STORAGE_KEYS = ['ngStorage-auth-token', 'ngStorage-token'];
    const filteredStorage = Object.fromEntries(
      Object.entries(localStorage).filter(([key]) => !EXCLUDED_STORAGE_KEYS.includes(key)),
    );

    this.logError({
      message: errorMessage,
      localStorage: JSON.stringify(filteredStorage),
    });
  }

  /**
   * Logs an error by dispatching a custom event outside Angular's change detection zone.
   *
   * @param {any} error the error to be logged
   */
  public logError(error: any): void {
    this.zone.runOutsideAngular(() =>
      window.dispatchEvent(
        new CustomEvent('ng-checkout-error', {
          detail: new AppError(error),
          bubbles: true,
        }),
      ),
    );
  }

  /**
   * Logs HTTP errors with additional context and formatted error details.
   *
   * @param {HttpErrorResponse} error the HTTP error response from HttpClient
   * @param {string} message optional custom error message
   * @param {object} context optional additional context data to include with the error log
   */
  public logHttpError(error: HttpErrorResponse, message?: string, context?: object): void {
    this.logError({
      message: message || error?.message || 'HTTP error occurred',
      ...(context || {}),
      errorBody: this.getErrorBodyAsString(error),
      status: error?.status,
      statusText: error?.statusText,
      requestErrorMessage: error?.message,
      requestUrl: error?.url,
    });
  }

  /**
   * Safely converts an HTTP error response body to a string representation.
   *
   * @param {HttpErrorResponse} error the HTTP error response
   *
   * @returns {string} a string representation of the error body, or a fallback message if conversion fails
   */
  private getErrorBodyAsString(error: HttpErrorResponse): string {
    if (!error?.error) {
      return 'No response body';
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    try {
      return JSON.stringify(error.error);
    } catch (jsonError) {
      return 'Unable to serialize response body';
    }
  }
}
